// src/components/Footer.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ExternalLink, Phone } from 'lucide-react';

const Footer = () => {
  const [logoError, setLogoError] = useState(false);
  
  const colors = {
    primary: '#2055df',
    secondary: '#0f172a',
    success: '#10b981',
    gray400: '#94a3b8',
    gray500: '#64748b'
  };

  const currentYear = new Date().getFullYear();

  const LogoComponent = () => {
    if (logoError) {
      // Fallback to text logo
      return (
        <>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: colors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 14,
            color: 'white'
          }}>
            Rx
          </div>
          <span style={{ color: 'white', fontSize: 20, fontWeight: 700 }}>ChatWithDr</span>
        </>
      );
    }

    return (
      <img
        src="/logo-white.png"
        alt="Rx.ChatWithDr"
        style={{
          height: 40,
          width: 'auto',
          maxWidth: 180,
          objectFit: 'contain'
          // REMOVED: filter: 'brightness(0) invert(1)' - logo-white.png is already white
        }}
        onError={() => setLogoError(true)}
      />
    );
  };

  return (
    <footer style={{
      background: colors.secondary,
      padding: '60px 24px 32px',
      fontFamily: "'Inter', -apple-system, sans-serif"
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Main Footer Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 48,
          marginBottom: 48
        }}>
          {/* Brand Column */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <LogoComponent />
            </div>
            <p style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: 14,
              lineHeight: 1.6,
              marginBottom: 20
            }}>
              Save up to 80% on your prescription medications with our free discount card. Accepted at over 67,000 pharmacies nationwide.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'white',
              marginBottom: 16,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Quick Links
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                { name: 'Home', path: '/' },
                { name: 'Search Drugs', path: '/search' },
                { name: 'How It Works', path: '/how-it-works' },
                { name: 'Top 50', path: '/top-prescriptions' },
                { name: 'Pharmacies', path: '/pharmacies' },
                { name: 'Get Card', path: '/card' }
              ].map((link, i) => (
                <li key={i} style={{ marginBottom: 10 }}>
                  <Link
                    to={link.path}
                    style={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: 14,
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Telehealth Services - Cross promotion */}
          <div>
            <h4 style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'white',
              marginBottom: 16,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              Telehealth Services
              <ExternalLink size={12} style={{ opacity: 0.7 }} />
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                { name: 'Online Doctor', url: 'https://chatwithdr.com' },
                { name: 'All Conditions', url: 'https://chatwithdr.com/conditions' },
                { name: 'UTI Treatment', url: 'https://chatwithdr.com' },
                { name: 'ED Treatment', url: 'https://chatwithdr.com' },
                { name: 'Birth Control', url: 'https://chatwithdr.com' },
                { name: 'Cold & Flu', url: 'https://chatwithdr.com' }
              ].map((link, i) => (
                <li key={i} style={{ marginBottom: 10 }}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: 14,
                      transition: 'color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = colors.success}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
            <p style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.5)',
              marginTop: 12,
              fontStyle: 'italic'
            }}>
              Chat with a doctor online 24/7
            </p>
          </div>

          {/* Support Column */}
          <div>
            <h4 style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'white',
              marginBottom: 16,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Contact
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: 12 }}>
                <a
                  href="mailto:support@rx.chatwithdr.com"
                  style={{
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Mail size={14} />
                  support@rx.chatwithdr.com
                </a>
              </li>
              <li style={{ marginBottom: 12 }}>
                <a
                  href="tel:+19195882428"
                  style={{
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Phone size={14} />
                  (919) 588-2428
                </a>
              </li>
              <li style={{ marginBottom: 12 }}>
                <div
                  style={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8
                  }}
                >
                  <MapPin size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>
                    2003 Plank Road #1283<br />
                    Fredericksburg, VA 22401
                  </span>
                </div>
              </li>
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h4 style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'white',
              marginBottom: 16,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Legal
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                { name: 'Terms of Service', path: '/terms' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Disclaimer', path: '/disclaimer' },
                { name: 'FAQ', path: '/faq' }
              ].map((link, i) => (
                <li key={i} style={{ marginBottom: 10 }}>
                  <Link
                    to={link.path}
                    style={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: 14,
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'white'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.1)',
          paddingTop: 32
        }}>
          {/* Bottom Section */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16
          }}>
            <p style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 13,
              margin: 0
            }}>
              © {currentYear} Rx.ChatWithDr.com. All rights reserved.
            </p>
            
            <p style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 12,
              margin: 0,
              maxWidth: 500,
              textAlign: 'right'
            }}>
              This is NOT insurance. Discounts are available exclusively through participating pharmacies. 
              The discount program provides access to discounted prices at participating pharmacies.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;